// @givmo/app-admin — Settings → "Security" tab: the REGISTRATION (AS-WEB4; the panels moved at AS-WEB8).
//
// The panels — the two-factor editor and roster panel, company sign-in, the session reset and the
// history export — moved to `@givmo/employer-module/features/security/settings-security-tab`, so the
// portal's tab and the company console's Admin Console › Security page render the same components.
// Every old export still resolves from this path (the `export *` below). What stays here is the
// portal's own half: the tab's place in the Settings registry and WHO may see it.
//
// ══ OWNER GATING — THE PORTAL'S, NOT THE PANELS' ══════════════════════════════════════════════
// The predicate fails CLOSED on a null role, matching the giving-card tab rather than the Match
// Settings tab's fail-OPEN in-body gate: it decides whether a privileged tab EXISTS in the strip
// (which renders before the role read resolves), so showing it on "not yet known" would flash a
// Security tab at a Member. A real Owner whose role read transiently failed loses the tab for that
// visit and gets it back on the next successful read — the documented behaviour of the registry gate.
//
// ⚠ It KICKS OFF both probes as a side effect, and only for an Owner. A Member never spends a request
// on a surface they could not use, and the probes cannot run until someone opens `/settings`. Their
// settling reaches the strip through the registry's store: the first Owner check subscribes the
// registry's notifier to the panels' probe store, BEFORE it starts them, so no settling is missed.

import {
  SECURITY_TAB_ID,
  SettingsSecurityTab,
  ensureSecurityProbes,
  securitySurfaceVerdict,
  subscribeSecurityProbes,
} from '@givmo/employer-module/features/security/settings-security-tab';
import {
  notifySettingsTabGatesChanged,
  type SettingsTabDefinition,
  type SettingsTabPermissionContext,
} from '../settings/settings-tab-registry';

export * from '@givmo/employer-module/features/security/settings-security-tab';

/** The probe store's notices reach the registry from the first Owner check on. */
let notifiesRegistry = false;

/**
 * Whether the Settings tab is visible to this caller: an Owner, once EITHER surface has answered 200.
 *
 * Every half fails CLOSED, and `unknown` counts as hidden — so nothing renders while a verdict is
 * being resolved and a dark environment renders nothing at all.
 */
export function canSeeSecurityTab(context: SettingsTabPermissionContext): boolean {
  if (context.role !== 'Owner') return false;
  if (!notifiesRegistry) {
    subscribeSecurityProbes(notifySettingsTabGatesChanged);
    notifiesRegistry = true;
  }
  ensureSecurityProbes();
  return securitySurfaceVerdict() === 'available';
}

/** The registry entry. Order 60 — the core four hold 10/20/30/40 and CW-WEB4's card holds 50. */
export const SECURITY_SETTINGS_TAB: SettingsTabDefinition = {
  id: SECURITY_TAB_ID,
  label: 'Security',
  order: 60,
  permission: (context) => canSeeSecurityTab(context),
  component: SettingsSecurityTab,
};
